"use client";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from "@nextui-org/react";
import { Icon } from '@iconify/react';
import { UploadedFileType } from '../utils/constants';
import { getAttachmentPreviewKindFromFile, getDocumentCategoryLabelFromFile, formatFileSize, getFileType } from '../utils/commonutils';
import {
  AttachmentAudioLeftIcon,
  AttachmentCornerRemoveButton,
  AttachmentDocumentLeftIcon,
  AttachmentFileTypeCard,
  AttachmentMediaModal,
  type AttachmentMediaOverlayState,
  openLocalFileInNewTab,
  useObjectUrl,
} from './attachmentFileCardPreview';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import 'viewerjs/dist/viewer.css';
import Viewer from 'viewerjs';

/** Shared file input `accept` list used wherever attachments can be picked (prompt box, comment box, triage comments). */
export const ATTACHMENT_FILE_INPUT_ACCEPT =
  'image/*,video/*,audio/*,application/zip,application/x-zip-compressed,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.json,.md,.rtf';

export const IMAGE_VIDEO_MAX_BYTES = 30 * 1024 * 1024;
export const FILE_DOCUMENT_MAX_BYTES = 1 * 1024 * 1024;

export const ATTACHMENT_SIZE_TOAST_IMAGE_VIDEO = 'Images and videos must be smaller than 30 MB.';
export const ATTACHMENT_SIZE_TOAST_FILE_DOCUMENT = 'Files and documents must be smaller than 1 MB.';

/** Splits files by the shared size limits: images/video < 30MB, everything else (files/documents/audio) < 1MB. */
export function filterFilesByAttachmentSizeLimit(files: File[]): {
  accepted: File[];
  rejectedImageVideo: boolean;
  rejectedFileDocument: boolean;
} {
  const accepted: File[] = [];
  let rejectedImageVideo = false;
  let rejectedFileDocument = false;
  for (const file of files) {
    const kind = getAttachmentPreviewKindFromFile(file);
    const isImageOrVideo = kind === 'image' || kind === 'video';
    const maxBytes = isImageOrVideo ? IMAGE_VIDEO_MAX_BYTES : FILE_DOCUMENT_MAX_BYTES;
    if (file.size <= maxBytes) {
      accepted.push(file);
    } else if (isImageOrVideo) {
      rejectedImageVideo = true;
    } else {
      rejectedFileDocument = true;
    }
  }
  return { accepted, rejectedImageVideo, rejectedFileDocument };
}

/**
 * Uploads files via presigned URL + S3 and reflects progress/result into `setUploadedFiles`.
 * Shared by the live prompt box (`chatForm.tsx`) and the comment/triage attachment picker
 * so the upload mechanics exist in exactly one place.
 */
export async function uploadAttachmentFiles(
  files: File[],
  deps: {
    getPresignedUrl: (payload: { filename: string; source_type: string }[]) => { unwrap: () => Promise<any> };
    uploadToS3: (args: { file: File; presignedUrl: string; CtyShowToast: typeof CtyShowToast }) => { unwrap: () => Promise<any> };
    setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileType[]>>;
    /** Fallback error toast text if the thrown error has no message. Defaults to a plain string. */
    errorMessage?: string;
  }
): Promise<void> {
  const { getPresignedUrl, uploadToS3, setUploadedFiles, errorMessage = 'Upload failed' } = deps;
  if (!files.length) return;

  const newEntries: UploadedFileType[] = files.map((f) => ({
    size: formatFileSize(f.size),
    name: f.name,
    type: getFileType(f.name).toUpperCase(),
    uploading: true,
    file: f,
  }));
  setUploadedFiles((prev) => [...prev, ...newEntries]);

  try {
    const payLoad = files.map((f) => ({ filename: f.name, source_type: 'advisor' }));
    const uploadURL: any = await getPresignedUrl(payLoad).unwrap();
    const urlList = Array.isArray(uploadURL) ? uploadURL : [uploadURL];
    await Promise.all(
      files.map((file, i) => uploadToS3({ file, presignedUrl: urlList[i]?.presigned_url, CtyShowToast }).unwrap())
    );
    setUploadedFiles((prev) =>
      prev.map((f) => {
        const idx = files.indexOf(f.file);
        if (idx === -1) return f;
        return { ...f, uploading: false, source_id: urlList[idx]?.source_id };
      })
    );
  } catch (err) {
    setUploadedFiles((prev) => prev.filter((f) => !files.includes(f.file)));
    CtyShowToast(checkType.error, (err as Error)?.message ?? errorMessage);
  }
}

/** Stable component so image thumbnails don't re-mount on parent re-render */
function ImageThumb(props: Readonly<{ file: UploadedFileType; onRemove?: () => void; onEnlarge: () => void }>) {
  const { file, onRemove, onEnlarge } = props;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (file?.file?.type?.startsWith?.('image/')) {
      const u = URL.createObjectURL(file.file);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
  }, [file?.file]);
  if (!url && !file.uploading) return null;
  return (
    <div className="relative group flex-shrink-0">
      {onRemove && (
        <button type="button" onClick={onRemove} className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-default-200 hover:bg-default-300 flex items-center justify-center opacity-90 group-hover:opacity-100" aria-label="Remove image">
          <Icon icon="zondicons:close-solid" width="12" height="12" />
        </button>
      )}
      <button type="button" onClick={() => !file.uploading && onEnlarge()} className="block w-12 h-12 rounded-md border border-default-200 overflow-hidden bg-default-100 focus:outline-none focus:ring-2 focus:ring-primary">
        {file.uploading && (
          <div className="w-full h-full flex items-center justify-center"><Spinner size="sm" /></div>
        )}
        {!file.uploading && url && (
          <img src={url} alt={file.name} className="w-full h-full object-cover" />
        )}
      </button>
    </div>
  );
}

function PromptVideoAttachmentRow(props: Readonly<{
  uf: UploadedFileType;
  onRemove?: () => void;
  onOpenModal: (url: string, name: string) => void;
  videoLabel: string;
}>) {
  const { uf, onRemove, onOpenModal, videoLabel } = props;
  const url = useObjectUrl(uf.file);
  return (
    <div className="relative group flex-shrink-0 max-w-[200px]">
      {onRemove && <AttachmentCornerRemoveButton onClick={onRemove} />}
      <AttachmentFileTypeCard
        label={videoLabel}
        fileName={uf.name}
        loading={uf.uploading}
        disabled={uf.uploading}
        onClick={() => !uf.uploading && url && onOpenModal(url, uf.name)}
        leftVariant="media"
        leftContent={
          url && !uf.uploading ? (
            <video src={url} className="w-full h-full object-cover pointer-events-none" muted playsInline preload="metadata" />
          ) : null
        }
      />
    </div>
  );
}

function PromptAudioAttachmentRow(props: Readonly<{
  uf: UploadedFileType;
  onRemove?: () => void;
  onOpenModal: (url: string, name: string) => void;
  audioLabel: string;
}>) {
  const { uf, onRemove, onOpenModal, audioLabel } = props;
  const url = useObjectUrl(uf.file);
  return (
    <div className="relative group flex-shrink-0 max-w-[200px]">
      {onRemove && <AttachmentCornerRemoveButton onClick={onRemove} />}
      <AttachmentFileTypeCard
        label={audioLabel}
        fileName={uf.name}
        loading={uf.uploading}
        disabled={uf.uploading}
        onClick={() => !uf.uploading && url && onOpenModal(url, uf.name)}
        leftVariant="neutral"
        leftContent={<AttachmentAudioLeftIcon />}
      />
    </div>
  );
}

function PromptDocumentAttachmentRow(props: Readonly<{ uf: UploadedFileType; onRemove?: () => void }>) {
  const { uf, onRemove } = props;
  return (
    <div className="relative group flex-shrink-0 max-w-[200px]">
      {onRemove && <AttachmentCornerRemoveButton onClick={onRemove} />}
      <AttachmentFileTypeCard
        label={getDocumentCategoryLabelFromFile(uf.file)}
        fileName={uf.name}
        loading={uf.uploading}
        disabled={uf.uploading}
        onClick={() => !uf.uploading && openLocalFileInNewTab(uf.file)}
        leftVariant="neutral"
        leftContent={<AttachmentDocumentLeftIcon />}
      />
    </div>
  );
}

/** Memoized attachment previews (images, video, audio, documents). Shared by the prompt box and the comment/triage attachment pickers. */
export const AttachmentPreviewSection = React.memo(function AttachmentPreviewSection_(props: Readonly<{
  uploadedFiles: UploadedFileType[];
  setUploadedFiles?: React.Dispatch<React.SetStateAction<UploadedFileType[]>>;
  enableImage?: boolean;
  /** Sent or read-only surfaces keep the thumbnail but not the remove control. */
  hideRemove?: boolean;
  /** Keep every thumbnail on one row and scroll sideways when they do not fit. */
  scroll?: boolean;
}>) {
  const { uploadedFiles, setUploadedFiles, enableImage = false, hideRemove = false, scroll = false } = props;
  const [mediaOverlay, setMediaOverlay] = useState<AttachmentMediaOverlayState>(null);
  const imageFiles = useMemo(
    () => uploadedFiles.filter((f) => f?.file && getAttachmentPreviewKindFromFile(f.file) === 'image'),
    [uploadedFiles]
  );
  const [viewerBlobUrls, setViewerBlobUrls] = useState<string[]>([]);
  const viewerBlobUrlsRef = useRef<string[]>([]);
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<InstanceType<typeof Viewer> | null>(null);

  useEffect(() => {
    viewerBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    viewerBlobUrlsRef.current = [];
    if (imageFiles.length === 0) {
      setViewerBlobUrls([]);
      return;
    }
    const urls = imageFiles.map((f) => URL.createObjectURL(f.file));
    viewerBlobUrlsRef.current = urls;
    setViewerBlobUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
      viewerBlobUrlsRef.current = [];
    };
  }, [imageFiles]);

  useEffect(() => {
    if (!enableImage || viewerBlobUrls.length === 0 || !viewerContainerRef.current) return;
    const timerId = setTimeout(() => {
      if (!viewerContainerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      try {
        viewerRef.current = new Viewer(viewerContainerRef.current, {
          title: false,
          toolbar: true,
          navbar: true,
          movable: true,
          zoomable: true,
          rotatable: true,
        });
      } catch (e) {
        console.warn('Viewer init failed:', e);
      }
    }, 100);
    return () => {
      clearTimeout(timerId);
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [enableImage, viewerBlobUrls]);

  const openEnlargedAt = (index: number) => {
    if (viewerRef.current) {
      try {
        viewerRef.current.view(index);
      } catch (err) {
        console.warn('Viewer view failed:', err);
      }
    }
  };

  const removeAt = (globalIndex: number) => {
    setUploadedFiles?.((prev) => prev.filter((_, i) => i !== globalIndex));
  };

  if (uploadedFiles.length === 0) return null;

  return (
    <>
      <div className={scroll
        ? 'flex min-w-0 max-w-full flex-nowrap items-start gap-2 overflow-x-auto'
        : 'm-2 mb-0 flex flex-wrap items-start gap-2'}>
        {uploadedFiles.map((uf, globalIdx) => {
          const kind = uf.file ? getAttachmentPreviewKindFromFile(uf.file) : 'file';
          const key = `${uf.name}-${uf.size}-${globalIdx}`;
          const remove = hideRemove ? undefined : () => removeAt(globalIdx);
          if (kind === 'image') {
            const imageIndex = uploadedFiles.slice(0, globalIdx).filter((f) => f.file && getAttachmentPreviewKindFromFile(f.file) === 'image').length;
            return (
              <ImageThumb
                key={key}
                file={uf}
                onRemove={remove}
                onEnlarge={() => openEnlargedAt(imageIndex)}
              />
            );
          }
          if (kind === 'video') {
            return (
              <PromptVideoAttachmentRow
                key={key}
                uf={uf}
                onRemove={remove}
                onOpenModal={(url, name) => setMediaOverlay({ kind: 'video', url, name })}
                videoLabel="Video"
              />
            );
          }
          if (kind === 'audio') {
            return (
              <PromptAudioAttachmentRow
                key={key}
                uf={uf}
                onRemove={remove}
                onOpenModal={(url, name) => setMediaOverlay({ kind: 'audio', url, name })}
                audioLabel="Audio"
              />
            );
          }
          return <PromptDocumentAttachmentRow key={key} uf={uf} onRemove={remove} />;
        })}
      </div>
      {enableImage && viewerBlobUrls.length > 0 && (
        <div ref={viewerContainerRef} className="hidden" aria-hidden="true">
          {viewerBlobUrls.map((url) => (
            <img key={url} src={url} alt="" />
          ))}
        </div>
      )}
      <AttachmentMediaModal overlay={mediaOverlay} onClose={() => setMediaOverlay(null)} />
    </>
  );
});
